/* /industries/:key (and "/ae/industries/:key"…) — public marketing page for one industry pack in one GCC country
 * (catalog: contracts/industries.json, re-worded for the country by mktLocal.ts). */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { INDUSTRIES, industryOf } from "../../../domain/industry";
import { CountryScope } from "../../../lib/country";
import { inCountry, loginRoute, signupRoute, useMktCountry } from "../mktCountry";
import { heatBanOf, inName, industryIn, isSaudi, MKT_SCOPE } from "../mktLocal";
import { useI18n } from "../../../i18n";
import { Icon, Seg } from "../../../ui";
import { MktFooter, MktHeader } from "../components/MktChrome";
import { MktLink } from "../components/MktLink";
import { useSeo } from "../../../seo/useSeo";
import "../home-extra.css";
import { IndustryCards } from "./IndustryCards";
import "./industries.css";

const DAYS: [string, string][] = [["Sunday", "الأحد"], ["Monday", "الاثنين"], ["Tuesday", "الثلاثاء"], ["Wednesday", "الأربعاء"], ["Thursday", "الخميس"], ["Friday", "الجمعة"], ["Saturday", "السبت"]];
const fmtMD = (md: string, L: (en: string, ar: string) => string) => {
  const [m, d] = md.split("-").map(Number);
  const months: [string, string][] = [["Jan", "يناير"], ["Feb", "فبراير"], ["Mar", "مارس"], ["Apr", "أبريل"], ["May", "مايو"], ["Jun", "يونيو"], ["Jul", "يوليو"], ["Aug", "أغسطس"], ["Sep", "سبتمبر"], ["Oct", "أكتوبر"], ["Nov", "نوفمبر"], ["Dec", "ديسمبر"]];
  return `${d} ${L(...months[m - 1])}`;
};

/** The page under the marketing scope (see MKT_SCOPE): its copy is worded for the page's country by mktLocal. */
export default function IndustryMarketing() {
  return <CountryScope country={MKT_SCOPE}><IndustryMarketingPage /></CountryScope>;
}

function IndustryMarketingPage() {
  const { key } = useParams();
  const { L } = useI18n();
  const cc = useMktCountry();
  const sa = isSaudi(cc);
  const the = inName(cc);
  const HEAT_BAN = heatBanOf(cc);
  const base = industryOf(key);
  const ind = useMemo(() => base && industryIn(base, cc), [base, cc]);
  useSeo(inCountry("/industries/" + key, cc));
  const [show, setShow] = useState<"all" | "live" | "planned">("all");
  useEffect(() => {
    if (!ind) document.title = L("StartHR · Industries", "ستارت HR · القطاعات");
    window.scrollTo?.(0, 0);
  }, [ind, L]);

  if (!ind) {
    return (
      <div className="mkt" data-testid="industry-missing">
        <MktHeader links={[]} onHome={false} />
        <section className="mkt-sec"><div className="mkt-in" style={{ textAlign: "center" }}>
          <h1 style={{ fontSize: 28 }}>{L("We don't have that industry page", "لا توجد صفحة لهذا القطاع")}</h1>
          <p className="muted">{L(`Choose one of our ${INDUSTRIES.length} industry packs.`, `اختر إحدى حزمنا القطاعية الـ ${INDUSTRIES.length}.`)}</p>
          <MktLink className="btn pri" to={inCountry("/industries", cc)}><span>{L("See all industries", "عرض كل القطاعات")}</span></MktLink>
        </div></section>
        <MktFooter onHome={false} />
      </div>
    );
  }

  const live = ind.features.filter((f) => f.status === "live");
  const feats = ind.features.filter((f) => show === "all" || f.status === show);
  const signup = signupRoute(cc, ind.key);
  const weekend = ind.shift.weekend.map((d) => L(...DAYS[d])).join(L(" & ", " و"));

  return (
    <div className="mkt" data-testid="industry-page">
      <MktHeader links={[]} onHome={false} category={ind.key} />

      <section className="hero2 ip-hero">
        <div className="sadu" aria-hidden="true" />
        <div className="mkt-in hero2-g">
          <div className="hero2-t">
            <nav className="xs" aria-label={L("Breadcrumb", "مسار التنقل")} style={{ color: "#a9c4ba" }}>
              <MktLink to={inCountry("/home", cc)} style={{ color: "inherit" }} data-testid="crumb-home">{L("Home", "الرئيسية")}</MktLink> / <MktLink to={inCountry("/industries", cc)} style={{ color: "inherit" }} data-testid="crumb-industries">{L("Industries", "القطاعات")}</MktLink>
            </nav>
            <span className="eyebrow"><Icon name={ind.icon} className="s" /> {L("StartHR industry pack", "حزمة ستارت HR القطاعية")}</span>
            <h1 data-testid="ip-title">{sa ? L(`HR & payroll for ${ind.name[0]}`, `الموارد البشرية والرواتب لقطاع ${ind.name[1]}`) : L(`HR & payroll for ${ind.name[0]} in ${the[0]}`, `الموارد البشرية والرواتب لقطاع ${ind.name[1]} في ${the[1]}`)}</h1>
            <p>{L(...ind.tagline)}</p>
            <p style={{ fontSize: 15 }}>{L(...ind.summary)}</p>
            <div className="ip-cta">
              <Link className="btn gold lg" to={signup} data-testid="ip-trial"><span>{L("Start free with this pack", "ابدأ مجانًا بهذه الحزمة")}</span><Icon name="chevron-right" className="s" /></Link>
              <Link className="btn lg on-dark" to={loginRoute(cc, ind.key)} data-testid="ip-demo"><span>{L("Try the live demo", "جرّب العرض المباشر")}</span></Link>
            </div>
            <span className="xs" style={{ color: "#a9c4ba" }} data-testid="ip-counts">
              {L(`${live.length} live features · ${ind.features.length - live.length} on the roadmap · plus every core StartHR module`, `${live.length} ميزة متاحة · ${ind.features.length - live.length} ضمن خطة التطوير · مع كل وحدات ستارت HR الأساسية`)}
            </span>
          </div>
          <div className="ip-stats" data-testid="ip-stats">
            {ind.stats.map(([v, l]) => <div key={l[0]} className="ip-stat"><b className="num">{v}</b><span>{L(...l)}</span></div>)}
          </div>
        </div>
      </section>

      <section className="mkt-sec" data-testid="ip-pains">
        <div className="mkt-in">
          <div className="sec-h"><span className="eyebrow">{L("The problem", "التحدي")}</span><h2>{L(`What slows ${ind.name[0].toLowerCase()} HR teams down`, `ما الذي يبطئ فرق الموارد البشرية في ${ind.name[1]}`)}</h2></div>
          <div className="ip-pains">{ind.pains.map((p) => <div key={p[0]} className="ip-pain"><span className="kpi-ico warn"><Icon name="alert" /></span><span>{L(...p)}</span></div>)}</div>
        </div>
      </section>

      <section className="mkt-sec alt" id="ip-features" data-testid="ip-features">
        <div className="mkt-in">
          <div className="sec-h"><span className="eyebrow">{L("What you get", "ما تحصل عليه")}</span><h2>{L(`${ind.features.length} features built for ${ind.name[0].toLowerCase()}`, `${ind.features.length} ميزة مصممة لقطاع ${ind.name[1]}`)}</h2></div>
          <div className="ip-bar">
            <span className="sm muted">{L("Live features work today. Roadmap items are planned and marked honestly.", "الميزات المتاحة تعمل اليوم، وعناصر خطة التطوير مُعلَّمة بوضوح.")}</span>
            <Seg label={L("Show", "عرض")} value={show} onChange={setShow} items={[["all", L(`All ${ind.features.length}`, `الكل ${ind.features.length}`)], ["live", L(`Live ${live.length}`, `متاحة ${live.length}`)], ["planned", L("Roadmap", "خطة التطوير")]]} />
          </div>
          <div className="ip-feats">
            {feats.map((f) => (
              <div key={f.key} className={"ip-feat" + (f.status === "planned" ? " planned" : "")} data-testid={"ipf-" + f.key}>
                <div className="ip-feat-h"><b>{L(...f.title)}</b>{f.status === "live" ? <span className="chip ok">{L("Live", "متاحة")}</span> : <span className="chip">{L("On the roadmap", "ضمن خطة التطوير")}</span>}</div>
                <p>{L(...f.text)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mkt-sec" data-testid="ip-compliance">
        <div className="mkt-in">
          <div className="sec-h"><span className="eyebrow">{L("Rules we track for you", "قواعد نتابعها نيابةً عنك")}</span><h2>{L("Credentials, localisation and working time", "الاعتمادات والتوطين وأوقات العمل")}</h2></div>
          <div className="ip-cols">
            <div className="ip-box" data-testid="ip-certs">
              <h3><Icon name="shield" />{L("Certificates & licences", "الشهادات والرخص")}</h3>
              <ul>{ind.certs.map((c) => <li key={c.kind}><b>{L(c.kind, c.ar)}</b>{c.note && <span className="xs">{L(...c.note)}</span>}</li>)}</ul>
              <span className="xs muted">{L("Each is matched to the right job titles and alerts before it expires.", "تُطابق كل شهادة مع المسميات الوظيفية المناسبة وتنبّه قبل انتهائها.")}</span>
            </div>
            {sa ? <div className="ip-box" data-testid="ip-quotas">
              <h3><Icon name="users" />{L("Saudization targets", "نسب التوطين")}</h3>
              {ind.quotas.length ? (
                <ul>{ind.quotas.map((q) => (
                  <li key={q.group[0]}><b>{L(...q.group)}: <span className="num">{q.pct}%</span></b>
                    <span className="xs">{L(`From ${q.minStaff} staff in the group; Saudis count from SAR ${q.minWage.toLocaleString("en")} a month.`, `ابتداءً من ${q.minStaff} موظفين في الفئة؛ ويُحتسب السعودي من ${q.minWage.toLocaleString("en")} ريال شهريًا.`)}</span>
                    {q.note && <span className="xs">{L(...q.note)}</span>}
                  </li>
                ))}</ul>
              ) : <span className="sm muted">{L("Nitaqat bands apply company-wide; StartHR tracks them on the Saudization page.", "تنطبق نطاقات نطاقات على مستوى المنشأة، ويتابعها ستارت HR في صفحة التوطين.")}</span>}
            </div> : <div className="ip-box" data-testid="ip-quotas">
              <h3><Icon name="users" />{L(...cc.localisation.name)}</h3>
              <ul><li><b>{L(...cc.localisation.short)}</b><span className="xs">{L(...cc.localisation.summary)}</span></li></ul>
              <span className="xs muted">{L(`Sector targets change by decision — confirm yours with the ${cc.ministry.name[0]}.`, `تتغير نسب القطاعات بقرارات رسمية — تحقق من نسبتك لدى ${cc.ministry.name[1]}.`)} <a href={cc.localisation.url} target="_blank" rel="noopener noreferrer" data-testid="ip-loc-link">{L("Official rules", "القواعد الرسمية")}<Icon name="external" className="s" /></a></span>
            </div>}
            <div className="ip-box" data-testid="ip-shift">
              <h3><Icon name="clock" />{L("Recommended working time", "وقت العمل المقترح")}</h3>
              <ul>
                <li><b className="num" dir="ltr">{ind.shift.start}–{ind.shift.end}</b><span className="xs">{L(`Weekend: ${weekend}`, `العطلة: ${weekend}`)}</span><span className="xs">{L(...ind.shift.note)}</span></li>
                {ind.heat && <li data-testid="ip-heat"><b>{L("Summer midday ban", "حظر العمل وقت الظهيرة صيفًا")}</b><span className="xs">{L(`No outdoor work ${HEAT_BAN.start}–${HEAT_BAN.end}, ${fmtMD(HEAT_BAN.from, L)} – ${fmtMD(HEAT_BAN.to, L)}. StartHR checks every outdoor site's shift against it.`, `لا عمل في الأماكن المكشوفة ${HEAT_BAN.start}–${HEAT_BAN.end} من ${fmtMD(HEAT_BAN.from, L)} إلى ${fmtMD(HEAT_BAN.to, L)}، ويتحقق ستارت HR من ورديات كل موقع مكشوف.`)}</span></li>}
                <li><b>{L(...ind.sites.label)}</b><span className="xs">{L("Allocate people, see the monthly labour cost" + (ind.sites.contract ? " and the margin against each contract." : " of each one."), "خصص الموظفين واعرف تكلفة العمالة الشهرية" + (ind.sites.contract ? " والهامش مقابل كل عقد." : " لكل موقع."))}</span></li>
              </ul>
            </div>
          </div>
          <div className="ip-box" style={{ marginTop: 18 }} data-testid="ip-pay">
            <h3><Icon name="wallet" />{L("Pay elements set up for you", "عناصر الأجر الجاهزة")}</h3>
            <div className="ip-pay">{ind.pay.map((p) => <span key={p[0]} className="chip">{L(...p)}</span>)}</div>
          </div>
        </div>
      </section>

      <section className="mkt-sec alt" data-testid="ip-others">
        <div className="mkt-in">
          <div className="sec-h"><span className="eyebrow">{L("Other industries", "قطاعات أخرى")}</span><h2>{L("Running more than one line of business?", "هل لديك أكثر من نشاط؟")}</h2></div>
          <IndustryCards exclude={ind.key} />
        </div>
      </section>

      <section className="cta2">
        <div className="sadu" aria-hidden="true" />
        <div className="mkt-in">
          <h2>{L(`Switch on the ${ind.name[0]} pack today.`, `فعّل حزمة ${ind.name[1]} اليوم.`)}</h2>
          <p>{L("30 days free. Choose your industry at sign-up; you can change it later in Settings.", "30 يومًا مجانًا. اختر قطاعك عند التسجيل ويمكنك تغييره لاحقًا من الإعدادات.")}</p>
          <div className="row" style={{ justifyContent: "center" }}>
            <Link className="btn gold lg" to={signup} data-testid="ip-trial-2"><span>{L("Start free trial", "ابدأ مجانًا")}</span></Link>
            <MktLink className="btn lg on-dark" to="/pricing"><span>{L("See pricing", "عرض الأسعار")}</span></MktLink>
          </div>
        </div>
      </section>

      <MktFooter onHome={false} />
    </div>
  );
}
